import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Flex, Modal, Pagination } from 'antd';
import { HttpError, useDelete } from '@refinedev/core';
import {
  IProfessorAreaNoticeBoardPost,
  IProfessorAreaNoticeBoardShow
} from 'interfaces/professor_area/notice_boards';
import { ModalShowNoticeBoard } from './ModalShow';
import { NoticeBoardItem } from './Item';
import { useCallback } from 'react';
import { useModalForm } from '@refinedev/antd';

interface NoticeBoardItemProps {
  notices?: IProfessorAreaNoticeBoardShow[];
  page: number;
  total?: number;
  itemsPerPage?: number;
  onChangePage: (page: number) => void;
  isLoading: boolean;
  refetch: () => void;
  canShow: boolean;
  canDelete: boolean;
}

export const NoticeBoardList = ({
  notices,
  page,
  total,
  itemsPerPage,
  onChangePage,
  refetch,
  isLoading,
  canShow,
  canDelete
}: NoticeBoardItemProps) => {
  const { confirm } = Modal;

  const { mutateAsync: deleteNotice } =
    useDelete<IProfessorAreaNoticeBoardShow>();

  const {
    modalProps: editModalProps,
    formProps: editFormProps,
    show: editModalShow,
    close: editModalClose
  } = useModalForm<
    IProfessorAreaNoticeBoardPost,
    HttpError,
    IProfessorAreaNoticeBoardPost
  >({
    resource: 'professor_area/notice_boards',
    action: 'edit'
  });

  const handleRemoveNotice = useCallback(
    (noticeId: string) => {
      confirm({
        title: 'Deseja excluir o aviso?',
        icon: <ExclamationCircleOutlined className="!text-red-500" />,
        content: 'Essa ação não pode ser revertida',
        okText: 'Excluir',
        cancelText: 'Voltar',
        okButtonProps: {
          danger: true
        },
        onOk() {
          deleteNotice({
            id: noticeId,
            resource: 'professor_area/notice_boards',
            successNotification: () => ({
              type: 'success',
              description: 'Sucesso!',
              message: 'Aviso excluído com sucesso.'
            }),
            errorNotification: () => ({
              type: 'error',
              description: 'Ops!',
              message: 'Não foi possível excluir o aviso. Tente novamente.'
            })
          }).then(() => {
            refetch();
            editModalClose();
          });
        }
      });
    },
    [confirm, deleteNotice, editModalClose, refetch]
  );

  return (
    <>
      <div>
        <Flex vertical>
          {notices?.map(notice => (
            <NoticeBoardItem
              key={notice.id}
              notice={notice}
              onShow={noticeId => editModalShow(noticeId)}
              onRemove={handleRemoveNotice}
              canShow={canShow}
              canDelete={canDelete}
            />
          ))}
        </Flex>

        <Pagination
          className="justify-self-end mt-4"
          hideOnSinglePage
          current={page}
          total={total}
          pageSize={itemsPerPage}
          onChange={onChangePage}
        />
      </div>

      {canShow && editModalProps.open && !isLoading && (
        <ModalShowNoticeBoard
          modalProps={editModalProps}
          formProps={editFormProps}
          isLoading={isLoading}
          onCloseModal={editModalClose}
          onRemoveNotice={handleRemoveNotice}
          canDelete={canDelete}
        />
      )}
    </>
  );
};
